import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { useState } from 'react'
import { toast } from 'sonner'

import { UserAvatar } from '@/components/user-avatar'

// Reported stories stay viewable here for a week after the report, even past their 24h.
export function AdminStoryReports() {
  const reports = useQuery(api.stories.reportsForAdmin)
  const resolve = useMutation(api.stories.resolveReports)
  const [busy, setBusy] = useState<string | null>(null)
  if (!reports) return null
  const open = reports.filter((r) => !r.reviewedAt).length

  const act = async (itemId: Id<'storyItems'>, outcome: 'dismissed' | 'removed') => {
    setBusy(itemId)
    try {
      await resolve({ itemId, outcome })
    } catch {
      toast.error('Could not update')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground/40">
        Story reports · {open} to review
      </h2>
      {reports.length === 0 && <p className="text-sm text-muted-foreground">No reports.</p>}
      {reports.map((r) => (
        <div
          key={r.itemId}
          className={cn(
            'flex gap-3 rounded-xl border border-border/60 bg-card/40 p-3',
            r.reviewedAt && 'opacity-60',
          )}
        >
          <div className="aspect-[9/16] w-24 shrink-0 overflow-hidden rounded-lg bg-muted">
            {r.videoUrl ? (
              <video
                src={r.videoUrl}
                poster={r.url ?? undefined}
                controls
                playsInline
                preload="metadata"
                className="size-full object-cover"
              />
            ) : r.url ? (
              <a href={r.url} target="_blank" rel="noreferrer">
                <img src={r.url} alt="" className="size-full object-cover" />
              </a>
            ) : (
              <div className="flex size-full items-center justify-center p-2 text-center text-[11px] text-muted-foreground">
                Deleted
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <UserAvatar name={r.ownerName} image={r.ownerImage} className="size-6" />
              <span className="truncate text-sm font-semibold text-foreground">{r.ownerName}</span>
              <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                {new Date(r.lastReportedAt).toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {r.reporters} {r.reporters === 1 ? 'report' : 'reports'} ·{' '}
              {Object.entries(r.reasons)
                .map(([reason, n]) => (n > 1 ? `${reason} ×${n}` : reason))
                .join(', ')}{' '}
              · {r.source === 'clip' ? 'own clip' : 'check-in'} · {r.status}
              {r.expired && r.status !== 'gone' ? ' · expired' : ''}
            </p>
            {r.caption && <p className="text-sm text-foreground/80">“{r.caption}”</p>}
            {r.purgeAt && !r.reviewedAt && (
              <p className="text-[11px] text-muted-foreground">
                Files kept until {new Date(r.purgeAt).toLocaleDateString()}
              </p>
            )}
            <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
              {r.reviewedAt ? (
                <span className="text-xs font-semibold text-muted-foreground">
                  {r.outcome === 'removed' ? 'Removed' : 'Dismissed'} ·{' '}
                  {new Date(r.reviewedAt).toLocaleDateString()}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={busy === r.itemId}
                    onClick={() => void act(r.itemId, 'removed')}
                    className="cursor-pointer rounded-full bg-destructive/15 px-3 py-1 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/25 disabled:opacity-50"
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    disabled={busy === r.itemId}
                    onClick={() => void act(r.itemId, 'dismissed')}
                    className="cursor-pointer rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                  >
                    {r.status === 'hidden' && !r.expired ? 'Dismiss · restore' : 'Dismiss'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </section>
  )
}
